import { z } from 'zod';
import { decimalSchema } from './prisma-zod';

// Invoice status enum - matches database schema
export enum InvoiceStatusEnum {
  DRAFT = 'DRAFT',
  SENT = 'SENT',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  PAID = 'PAID',
  OVERDUE = 'OVERDUE',
  CANCELLED = 'CANCELLED',
  VOID = 'VOID',
}

// Invoice status colors
export const INVOICE_STATUS_COLORS: Record<InvoiceStatusEnum, string> = {
  [InvoiceStatusEnum.DRAFT]: '#6b7280',
  [InvoiceStatusEnum.SENT]: '#3b82f6',
  [InvoiceStatusEnum.PARTIALLY_PAID]: '#f59e0b',
  [InvoiceStatusEnum.PAID]: '#10b981',
  [InvoiceStatusEnum.OVERDUE]: '#ef4444',
  [InvoiceStatusEnum.CANCELLED]: '#9ca3af',
  [InvoiceStatusEnum.VOID]: '#64748b',
};

// Invoice status display names
export const INVOICE_STATUS_DISPLAY_NAMES: Record<InvoiceStatusEnum, string> = {
  [InvoiceStatusEnum.DRAFT]: 'Draft',
  [InvoiceStatusEnum.SENT]: 'Sent',
  [InvoiceStatusEnum.PARTIALLY_PAID]: 'Partially Paid',
  [InvoiceStatusEnum.PAID]: 'Paid',
  [InvoiceStatusEnum.OVERDUE]: 'Overdue',
  [InvoiceStatusEnum.CANCELLED]: 'Cancelled',
  [InvoiceStatusEnum.VOID]: 'Void',
};

// Invoice sort field enum
export enum InvoiceSortFieldEnum {
  TITLE = 'title',
  DESCRIPTION = 'description',
  STATUS = 'status',
  DUE_DATE = 'dueDate',
  CREATED_AT = 'createdAt',
  UPDATED_AT = 'updatedAt',  
}

// Invoice line item schema
export const InvoiceLineItemSchema = z.object({
  id: z.string().uuid().optional(),
  // Price-book reference; null/absent = custom line. Description/price/tax
  // are snapshotted from the product at pick time and FROZEN — later product
  // edits never rewrite an existing invoice line.
  productId: z.string().uuid().optional().nullable(),
  description: z.string(),
  quantity: decimalSchema,
  unitPrice: decimalSchema,
  taxRate: decimalSchema.optional(),
  taxAmount: decimalSchema.optional(),
  subtotal: decimalSchema,
  total: decimalSchema,
  order: z.number().default(0),
  roleId: z.string().uuid().optional().nullable(),
  roleName: z.string().optional().nullable(),
});

export type InvoiceLineItem = z.infer<typeof InvoiceLineItemSchema>;

// Unbilled time summary (informational, snapshotted at invoice creation)
export const UnbilledTimeGroupSchema = z.object({
  label: z.string(),
  hours: z.number(),
});

export type UnbilledTimeGroup = z.infer<typeof UnbilledTimeGroupSchema>;

export const UnbilledTimeSummarySchema = z.object({
  period: z.object({ start: z.string(), end: z.string() }).nullable(),
  billedHours: z.number(),
  unbilledHours: z.number(),
  totalHours: z.number(),
  groups: z.array(UnbilledTimeGroupSchema).default([]),
});

export type UnbilledTimeSummary = z.infer<typeof UnbilledTimeSummarySchema>;

// Invoice settings schema
export const InvoiceSettingsSchema = z.object({
  lineItems: z.array(InvoiceLineItemSchema).default([]),
  calculatedTaxAmount: z.number().optional(),
  unbilledTimeSummary: UnbilledTimeSummarySchema.optional(),
  // Provenance: which flow minted this invoice ('quote', 'billing_schedule')
  source: z.string().optional(),
  quoteId: z.string().uuid().optional(),
  quoteNumber: z.string().optional(),
  // Written at schedule generation; the invoice detail back-link reads it.
  billingScheduleLineIds: z.array(z.string().uuid()).optional(),
}).catchall(z.unknown());

// Invoice schema
export const InvoiceSchema = z.object({
  id: z.string().uuid(),
  organizationId: z.string().uuid(),
  entityId: z.string().uuid(),
  projectId: z.string().uuid().nullable().optional(),
  contractId: z.string().uuid().nullable().optional(),
  billingAuthorizationId: z.string().uuid().nullable().optional(),
  // Hydrated by the invoice repository select (read-only display data).
  billingAuthorization: z
    .object({
      id: z.string().uuid(),
      label: z.string().nullable().optional(),
      referenceNumber: z.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  invoiceNumber: z.string().optional(),
  title: z.string(),
  description: z.string().optional(),
  issueDate: z.date(),
  dueDate: z.date(),
  subtotal: decimalSchema,
  taxRate: decimalSchema.optional(),
  taxAmount: decimalSchema.optional(),
  discount: decimalSchema.optional(),
  discountType: z.string().optional(),
  discountValue: decimalSchema.optional(),
  totalAmount: decimalSchema,
  amountPaid: decimalSchema,
  currency: z.string(),
  notes: z.string().optional(),
  termsAndConditions: z.string().optional(),
  paymentTerms: z.string().optional(),
  paymentMethod: z.string().optional(),
  paymentDueIn: z.number().optional(),
  status: z.nativeEnum(InvoiceStatusEnum).default(InvoiceStatusEnum.DRAFT),  
  sendReminderAt: z.date().optional(),
  lastReminderSent: z.date().optional(),  
  settings: InvoiceSettingsSchema.default({}),
  items: z.array(InvoiceLineItemSchema).optional(),
  // Open tracking, derived from the invoice's most recent outbound email
  // (read-only; never accepted on create/update).
  openCount: z.number().optional(),
  firstOpenedAt: z.date().optional(),
  lastOpenedAt: z.date().optional(),
  lastSentAt: z.date().optional(),
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});

export type Invoice = z.infer<typeof InvoiceSchema>;

// Create Invoice schema
export const CreateInvoiceSchema = InvoiceSchema.omit({
  id: true,
  organizationId: true,
  openCount: true,
  firstOpenedAt: true,
  lastOpenedAt: true,
  lastSentAt: true,
  createdAt: true,
  updatedAt: true,
  deletedAt: true,
});

export type CreateInvoice = z.infer<typeof CreateInvoiceSchema>;

// Update Invoice schema
export const UpdateInvoiceSchema = CreateInvoiceSchema.partial();

export type UpdateInvoice = z.infer<typeof UpdateInvoiceSchema>;

// Update Invoice input schema/type for API + UI payloads
export const UpdateInvoiceInputSchema = UpdateInvoiceSchema;

export type UpdateInvoiceInput = z.infer<typeof UpdateInvoiceInputSchema>;

// Invoice response schema
export const InvoiceResponseSchema = z.object({
  data: InvoiceSchema,
  message: z.string().optional(),
});

export type InvoiceResponse = z.infer<typeof InvoiceResponseSchema>;

// Invoices response schema
export const InvoicesResponseSchema = z.object({
  data: z.array(InvoiceSchema),
  total: z.number().optional(),
  message: z.string().optional(),
});

export type InvoicesResponse = z.infer<typeof InvoicesResponseSchema>; 
